import React, { useContext, useMemo } from "react";
import Table from "../component/Table";
import { FaUsersCog } from "react-icons/fa";
import { AuthContext } from "../context/Auth";
import Select from "react-select";
import { toast } from "react-toastify";
import axios from "../utils/Axios";

const UserInfo = () => {
  const { users, userRole, setIsEdited, isEdited, allOptions, managerModule } = useContext(AuthContext);
  const [loading, setLoading] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [rowInfo, setRowInfo] = React.useState(null);
  const [openModal, setOpenModal] = React.useState(false);
  const [isCreateMode, setIsCreateMode] = React.useState(false);
  const [formData, setFormData] = React.useState({
    username: "",
    email: "",
    role: "",
    module: [],
    assignedModules: "",
  });

  const mergeModuleData = useMemo(() => {
    if (allOptions.length === 0) return [];
    const formattedOptions = allOptions.map((value) => ({
      value: value,
      label: value
    }));
    return [...formattedOptions, { value: "*", label: "*" }];
  }, [allOptions]);

  const handleEditFunction = (row) => {
    setIsCreateMode(false);
    setRowInfo(row);
    const matchedUser = users.find((user) => user.email === row.email);

    setFormData({
      username: matchedUser.username,
      email: matchedUser.email,
      role: matchedUser.role,
      module: matchedUser.modules.map((mod) => ({ value: mod, label: mod })),
      assignedModules: matchedUser.modules,
    });
    setOpenModal(true);
  };

  const closeModalHandler = () => {
    setOpenModal(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleModuleChange = (selectedOptions) => {
    setFormData((prevState) => ({
      ...prevState,
      module: selectedOptions,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { username, email, role, module } = formData;
    const modules = module.map((mod) => mod.label?.toLowerCase());
    const payload = {
      modules,
      username,
      email,
      roleName: "pbi_" + role,
    };

    try {
      setLoading(true);
      const response = await axios.put("/pbi-dashbord/editUserInfo", payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (response.status === 200) {
        setIsEdited(!isEdited);
        toast.success("User edited successfully");
      }
    } catch (error) {
      console.log("Error while creating user.", error);
      toast.error("Error while creating user.");
    } finally {
      setLoading(false);
    }

    setOpenModal(false);
  };

  const handleCreateUser = () => {
    setIsCreateMode(true);
    setFormData({
      username: "",
      email: "",
      role: "",
      module: [],
      assignedModules: "",
    });
    setOpenModal(true);
  };

  return (
    <div className="bg-green-50 min-h-screen">
      <div className="flex px-4 sticky bg-green-50 top-0 coda-regular items-center justify-between py-4 border-b border-green-300 w-full">
        <h1 className="font-extrabold hidden md:flex text-2xl">User Informations</h1>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search anything..."
          className="px-4 w-96 py-2 border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <Table headers={["Username", "Email", "Role", "Action"]} data={users} searchQuery={searchQuery} onEdit={handleEditFunction} />
      <button
        className="bg-green-600 hidden text-white px-4 py-2 rounded-md mt-4"
        onClick={handleCreateUser}
      >
        Create New User
      </button>

      {openModal && (
        <div
          className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center"
          onClick={closeModalHandler}
        >
          <div
            className="bg-green-50 p-6 rounded-lg w-full m-4 md:m-0 shadow-lg md:w-1/3"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold text-green-800 mb-4">
              {isCreateMode ? "Create New User" : "Edit User Info"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Username</label>
                <input
                  type="text"
                  value={formData.username}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  name="username"
                  disabled
                  onChange={handleInputChange}
                  readOnly={!isCreateMode}
                  placeholder="Enter your username"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  name="email"
                  disabled
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  readOnly={!isCreateMode}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Role</label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                >
                  <option value="" disabled>Select Role</option>
                  {/* Removed hardcoded options and replaced with dynamic role options */}
                  {managerModule?.map((value, idx) => (
                    <option key={idx + value} value={value?.toLowerCase()}>
                      {value + " Manager"}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Assigned Module to <span className="text-red-600">{formData?.username}</span>
                </label>
                <Select
                  isMulti
                  options={mergeModuleData}
                  value={formData.module}
                  onChange={handleModuleChange}
                  className="w-full"
                  placeholder="Select Modules"
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={closeModalHandler}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 mr-2"
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  {loading ? "Editing..." : "Edit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserInfo;
